/**
 * Active Timer Hooks
 *
 * React Query hooks for timer operations (start/stop/listen).
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTeam } from '@/contexts/TeamContext';
import { useAuth } from '@/contexts/AuthContext';
import { logger } from '@/lib/logger';
import { getActiveTimer, listenActiveTimer, listActiveTimers } from '../db/activeTimers';
import {
  startWorkOrderTimer,
  stopWorkOrderTimer,
  discardWorkOrderTimer,
} from '../services/timeTracking.service';
import { timeEntryKeys } from './useTimeEntries';
import type { ActiveTimer, StartTimerInput } from '../types/timeEntry';

/**
 * Query key factory for active timers
 */
export const activeTimerKeys = {
  all: (teamId: string) => ['activeTimers', teamId] as const,
  mechanic: (teamId: string, mechanicId: string) => ['activeTimers', teamId, mechanicId] as const,
};

/**
 * Hook to get the current user's active timer
 * Uses polling for simplicity (can be upgraded to realtime listener)
 */
export function useActiveTimer() {
  const { activeTeamId } = useTeam();
  const { user } = useAuth();

  return useQuery({
    queryKey: activeTimerKeys.mechanic(activeTeamId || '', user?.uid || ''),
    queryFn: async () => {
      if (!activeTeamId || !user?.uid) return null;
      return getActiveTimer(activeTeamId, user.uid);
    },
    enabled: !!activeTeamId && !!user?.uid,
    staleTime: 10_000, // 10 seconds - timer state changes infrequently
    refetchInterval: 30_000, // Poll every 30 seconds for cross-device sync
  });
}

/**
 * Hook to listen to the current user's active timer in realtime
 * Returns the timer state and updates automatically
 */
export function useActiveTimerRealtime() {
  const { activeTeamId } = useTeam();
  const { user } = useAuth();
  const [timer, setTimer] = useState<ActiveTimer | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!activeTeamId || !user?.uid) {
      setTimer(null);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    const unsubscribe = listenActiveTimer(activeTeamId, user.uid, (newTimer) => {
      setTimer(newTimer);
      setIsLoading(false);
    });

    return () => unsubscribe();
  }, [activeTeamId, user?.uid]);

  return { data: timer, isLoading };
}

/**
 * Hook to list all active timers for the team
 * Useful for "who is working" dashboard
 */
export function useAllActiveTimers() {
  const { activeTeamId } = useTeam();

  return useQuery({
    queryKey: activeTimerKeys.all(activeTeamId || ''),
    queryFn: async () => {
      if (!activeTeamId) return [];
      return listActiveTimers(activeTeamId);
    },
    enabled: !!activeTeamId,
    staleTime: 30_000,
    refetchInterval: 60_000, // Poll every minute
  });
}

/**
 * Invalidate active timer queries
 */
function invalidateTimerQueries(
  queryClient: ReturnType<typeof useQueryClient>,
  teamId: string,
  mechanicId: string
) {
  void queryClient.invalidateQueries({
    queryKey: activeTimerKeys.mechanic(teamId, mechanicId),
  });
  void queryClient.invalidateQueries({
    queryKey: activeTimerKeys.all(teamId),
  });
}

/**
 * Hook to start a timer
 */
export function useStartTimer() {
  const { activeTeamId } = useTeam();
  const { user } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: StartTimerInput & { mechanicName?: string }) => {
      if (!activeTeamId) throw new Error('No active team');
      if (!user) throw new Error('No authenticated user');

      return startWorkOrderTimer(
        activeTeamId,
        input.workOrderId,
        user.uid,
        input.mechanicName,
        input
      );
    },
    onSuccess: (result, variables) => {
      if (activeTeamId && user) {
        invalidateTimerQueries(queryClient, activeTeamId, user.uid);

        // If we auto-stopped a previous timer, invalidate its time entries
        if (result.autoStoppedPrevious) {
          void queryClient.invalidateQueries({
            queryKey: timeEntryKeys.workOrder(activeTeamId, result.autoStoppedPrevious.workOrderId),
          });
        }

        // If status was updated, invalidate work orders list
        if (result.statusUpdated) {
          void queryClient.invalidateQueries({
            queryKey: ['workOrders', activeTeamId],
          });
        }

        // Invalidate the current work order's time entries
        void queryClient.invalidateQueries({
          queryKey: timeEntryKeys.workOrder(activeTeamId, variables.workOrderId),
        });
      }
      logger.info('[useStartTimer] Timer started');
    },
    onError: (error) => {
      logger.error('[useStartTimer] Failed to start timer:', error);
    },
  });
}

/**
 * Hook to stop the current user's timer
 */
export function useStopTimer() {
  const { activeTeamId } = useTeam();
  const { user } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      if (!activeTeamId) throw new Error('No active team');
      if (!user) throw new Error('No authenticated user');

      return stopWorkOrderTimer(activeTeamId, user.uid);
    },
    onSuccess: (result) => {
      if (activeTeamId && user) {
        invalidateTimerQueries(queryClient, activeTeamId, user.uid);

        // Invalidate time entries for the work order
        if (result?.timeEntry.workOrderId) {
          void queryClient.invalidateQueries({
            queryKey: timeEntryKeys.workOrder(activeTeamId, result.timeEntry.workOrderId),
          });
        }
      }
      logger.info('[useStopTimer] Timer stopped');
    },
    onError: (error) => {
      logger.error('[useStopTimer] Failed to stop timer:', error);
    },
  });
}

/**
 * Hook to discard the current user's timer without creating a time entry
 */
export function useDiscardTimer() {
  const { activeTeamId } = useTeam();
  const { user } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      if (!activeTeamId) throw new Error('No active team');
      if (!user) throw new Error('No authenticated user');

      return discardWorkOrderTimer(activeTeamId, user.uid);
    },
    onSuccess: () => {
      if (activeTeamId && user) {
        invalidateTimerQueries(queryClient, activeTeamId, user.uid);
      }
      logger.info('[useDiscardTimer] Timer discarded');
    },
    onError: (error) => {
      logger.error('[useDiscardTimer] Failed to discard timer:', error);
    },
  });
}
